import { useEffect, useState } from 'react';

import useComponentMounted from '../../../../hooks/useComponentMounted';
import { PREVIEW_DEBOUNCE_MS, isClientAudience } from '../utils/constants';
import { countRecipients } from '../utils/filterRecipients';
import { reportError } from '../utils/reportError';

const initialState = {
  count: 0,
  capped: false,
  loading: false,
  error: false,
};

export const useRecipientCount = (coachId, audience, requireFirstName = false) => {
  const [state, setState] = useState(initialState);
  const isMountedRef = useComponentMounted();

  const {
    audienceType, startDate, endDate, countries, salesStages, requireConsent,
  } = audience;
  const ready = !!coachId
    && countries.length > 0
    && (isClientAudience(audienceType) || salesStages.length > 0)
    && !!startDate
    && !!endDate;

  useEffect(() => {
    if (!ready) {
      setState(initialState);
      return undefined;
    }

    let cancelled = false;
    // Marked loading up front so the panel reads as stale for the whole debounce window, not just
    // once the queries go out.
    setState((prev) => ({ ...prev, loading: true, error: false }));

    const timeoutId = setTimeout(async () => {
      try {
        const { count, capped } = await countRecipients(coachId, {
          audienceType, startDate, endDate, countries, salesStages, requireConsent,
        }, requireFirstName);
        if (cancelled || !isMountedRef.current) return;
        setState({
          count, capped, loading: false, error: false,
        });
      } catch (err) {
        if (cancelled || !isMountedRef.current) return;
        reportError('countRecipients', err);
        setState({
          count: 0, capped: false, loading: false, error: true,
        });
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    ready,
    coachId,
    audienceType,
    startDate,
    endDate,
    countries,
    salesStages,
    requireConsent,
    requireFirstName,
    isMountedRef,
  ]);

  return state;
};
